/**
 * @description Displays a modal containing a searchable list of SECCON personnel fields and descriptions
 */

import React from 'react';
import { PropTypes } from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Slide from '@mui/material/Slide';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import IconButton from '@mui/material/IconButton';
import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import SearchInput from '@seccon/shared/ui/components/SearchInput';

import list from '../conf/fieldList';

const styles = {
  closeBtn: {
    mr: 2
  },
  dialogContent: {
    padding: 3,
    minHeight: '50vh',
    maxHeight: '50vh'
  },
  header: {
    alignItems: 'center',
    borderBottom: 1,
    borderColor: 'grey.400',
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'space-between',
    width: '100%'
  },
  list: {
    listStyleType: 'disc',
    ml: 3,
    mr: 3
  },
  listItem: {
    display: 'list-item'
  },
  noResults: {
    pt: 2
  },
  searchBox: {
    width: '100%'
  }
};

// Defines a slide up transition component
const Transition = React.forwardRef(
  (props, ref) => (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Slide direction='up' ref={ref} {...props} />
  )
);

/**
 * @prop onClose {Function} - handles onClose events
 * @prop open {boolean} - indicates if the modal is open or not
 */
const FieldListModal = ({ onClose, open }) => {
  // Manages the search state for filtering the list of fields
  const [search, setSearch] = React.useState('');

  /**
   * Handles the dialog closing by clearing the search and running the onClose callback
   */
  function handleClose () {
    setSearch('');
    onClose();
  }

  // A filtered list of seccon fields based on the current search criteria (case insensitive)
  const filteredList = React.useMemo(() => {
    // If the search is cleared
    if (search === '') {
      return list;
    }

    return list.filter((item) => item.field.toLowerCase().includes(search));
  }, [list, search]);

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={() => handleClose()}
      fullWidth
      maxWidth='md'
    >
      {/* Don't render the dialog content while its closed */}
      {open ? (
        <>
          <Box sx={styles.header}>
            <DialogTitle>
              Security Control Personnel Fields
            </DialogTitle>
            <IconButton onClick={() => handleClose()} sx={styles.closeBtn}>
              <Close />
            </IconButton>
          </Box>
          <DialogContent sx={styles.dialogContent}>
            <SearchInput
              search={search}
              setSearch={setSearch}
              placeholder='Search Fields'
              sx={styles.searchBox}
              variant='outlined'
            />
            {filteredList.length > 0 ? (
              <DialogContentText component='span'>
                <List sx={styles.list}>
                  {filteredList.map((item) => (
                    <ListItem key={item.field} sx={styles.listItem}>
                      <Typography component='span' fontWeight='fontWeightMedium'>{item.field}:</Typography> {item.description}
                    </ListItem>
                  ))}
                </List>
              </DialogContentText>
            ) : (
              <DialogContentText sx={styles.noResults}>
                No Fields Found
              </DialogContentText>
            )}
          </DialogContent>
        </>
      ) : null}
    </Dialog>
  );
}

FieldListModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired
};

export default FieldListModal;
